import * as Sentry from '@sentry/browser';
import { matchMaxMedia } from '@strava/ui/MediaQuery';
import { devLog, isDev } from './devUtils';
import {
  hasExtensionReferenceInBreadCrumbs,
  hasExtensionReferenceInHintErrorStack,
  hasExtensionReferenceInEventErrorStack,
  isXhrFalsePositive,
  isMfeChunkLoadError,
  groupErrors
} from './sentry.helper';

const displayType = () => {
  if (matchMaxMedia?.screenSm()) {
    return 'phone';
  }
  if (matchMaxMedia?.screenMd()) {
    return 'tablet';
  }
  return 'desktop';
};

if (!window.StravaSentryDsn) {
  window.StravaSentryDsn = {};
}
if (!window.StravaSentryDsn.deferred) {
  window.StravaSentryDsn.deferred = {};
  const promise = new Promise(
    function _(resolve, reject) {
      this.doResolve = resolve;
      this.doReject = reject;
    }.bind(window.StravaSentryDsn.deferred)
  );
  window.StravaSentryDsn.deferred.then = promise.then.bind(promise);
}

const {
  enabled,
  environment,
  release,
  userId,
  dsn,
  debug,
  pageProperties,
  mobileOS,
  isMobile
} = window.StravaSentry || {};

devLog(
  `Logged-out page experiment ::: ${JSON.stringify(
    pageProperties?.experiment_info || {}
  )}`
);

window.StravaSentryDsn.deferred.doResolve(dsn);

window.StravaSentryDsn.deferred.then((_dsn) => {
  // configure sentry_frontend.yml to enable sentry on local
  if (enabled) {
    Sentry.init({
      dsn: _dsn,
      maxBreadcrumbs: 50,
      integrations: [
        Sentry.browserTracingIntegration()
      ],
      denyUrls: [
        // Chrome extensions
        /extensions\//i,
        /^chrome:\/\//i
      ],
      sampleRate: 0.1,
      tracesSampler: (samplingContext) => {
        const { name } = samplingContext;
        // trace only transactions that are part of the onboarding flow
        // NOTE - use values < 1 for pages with high traffic to preserve quota
        if (name.includes("onboarding/continue")) {
          return 1; 
        }
        
        const adpRegex = /activities\/\d+\/?$/;
        if (adpRegex.test(name)) {
          return 0.1;
        }

        return 0;
      },
      debug,
      attachStacktrace: true,
      release,
      initialScope: {
        user: { id: userId }
      },
      environment,
      beforeSend: (event, hint) => {
        if (
          isMfeChunkLoadError(hint) ||
          isXhrFalsePositive(hint) || // safari abort - status 0
          hasExtensionReferenceInHintErrorStack(hint) ||
          hasExtensionReferenceInBreadCrumbs(event) ||
          hasExtensionReferenceInEventErrorStack(event) ||
          hint?.originalException === 'Timeout' // google-recaptcha flakiness
        ) {
          return null;
        }
        groupErrors(event);
        return event;
      },
      /**
       * There is a bug in Safari, that causes `AbortError` when fetch is
       * aborted, and you are in the middle of reading the response. In Chrome
       * and other browsers, it is handled gracefully, where in Safari, it
       * produces additional error, that is jumping outside of the original
       * Promise chain and bubbles up to the `unhandledRejection` handler, that
       * we then captures as error.
       *
       * Ref: https://bugs.webkit.org/show_bug.cgi?id=215771
       */
      ignoreErrors: ['AbortError: Fetch is aborted']
    });

    Sentry.setTag('strava_device.mobile_os', mobileOS);
    Sentry.setTag('strava_device.is_mobile', isMobile);
    Sentry.setTag('strava_device.display_type', displayType());

    if (pageProperties) {
      const experiment = pageProperties?.experiment_info || {};
      const {
        experiment_cohort: cohort,
        experiment_name: name,
        experiment_forced: forced
      } = experiment;
      if (cohort && name) {
        Sentry.setTag('experiment.name', name);
        Sentry.setTag('experiment.cohort', cohort);
        Sentry.setTag('experiment.forced', forced);
      }
    }
  }
});

/** @type {import("@sentry/browser").withScope} */
export const withScope = (...args) => {
  if (isDev()) {
    const callback = args[0];
    if (typeof callback === 'function') {
      callback({
        setTags: (tags) => devLog(`Sentry.withScope.setTags ::: ${JSON.stringify(tags)}`),
        setTag: (key, value) => devLog(`Sentry.withScope.setTag ::: ${key} = ${value}`),
        setContext: (name, context) => devLog(`Sentry.withScope.setContext ::: ${name} = ${JSON.stringify(context)}`)
      });
    }
  } else {
    Sentry.withScope(...args);
  }
};

/** @type {import("@sentry/browser").setTag} */
export const setTag = (...args) =>
  isDev()
    ? devLog(`Sentry.setTag ::: ${JSON.stringify(args)}`)
    : Sentry.setTag(...args);

/** @type {import("@sentry/browser").setTags} */
export const setTags = (...args) =>
  isDev()
    ? devLog(`Sentry.setTags ::: ${JSON.stringify(args)}`)
    : Sentry.setTags(...args);

/** @type {import("@sentry/browser").setContext} */
export const setContext = (...args) =>
  isDev()
    ? devLog(`Sentry.setContext ::: ${JSON.stringify(args)}`)
    : Sentry.setContext(...args);

/**
 * Log error
 * @param {Object} err Error object
 * @param {Object} extra Error additional data that can help understand the error
 */
export function logError(err, extra) {
  // When not in production skip calling Sentry code
  if (isDev()) {
    // eslint-disable-next-line no-console
    console.error('Sentry.logError ::: ', err, extra);
    return;
  }

  // `with-scope` creates a clone of the current scope and will stay isolated until the function call is completed.
  // https://docs.sentry.io/platforms/javascript/enriching-events/scopes/#local-scopes
  Sentry.withScope((scope) => {
    if (err && err.statusCode) {
      scope.setExtra('statusCode', err.statusCode);
    }

    if (extra) {
      scope.setExtras(extra);
    }

    Sentry.captureException(err);
  });
}

/** @type {import("@sentry/browser").captureMessage} */
export const logMessage = (message, captureContext) => {
  if (isDev()) {
    // eslint-disable-next-line no-console
    console.error('Sentry.logMessage :::', message, captureContext);
  } else {
    Sentry.captureMessage(message, captureContext);
  }
};

window.StravaSentry = {
  ...window.StravaSentry,
  logError,
  setTags,
  withScope
};

export default { logError, logMessage, withScope, setTag, setTags, setContext };
